import { defineStore } from 'pinia';
import { ref } from 'vue';
import { 
  getAuth, 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
  type AuthError
} from 'firebase/auth';
import { useNotificationStore } from './notificationStore';
import { useUserStore } from './userStore';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const userRole = ref<string | null>(null);
  const loading = ref(true);
  const notificationStore = useNotificationStore();
  const userStore = useUserStore();

  const auth = getAuth();

  // Listen to auth state changes
  onAuthStateChanged(auth, async (newUser) => {
    user.value = newUser;
    
    if (newUser) {
      // Update last sign in
      const userRef = doc(db, 'users', newUser.uid);
      await setDoc(userRef, {
        lastSignIn: new Date().toISOString()
      }, { merge: true });

      // Check if this is the first user and make them admin if so
      await userStore.setFirstUserAsAdmin(newUser.uid);
      
      // Get user role
      userRole.value = await userStore.getUserRole(newUser.uid);
    } else {
      userRole.value = null;
    }
    
    loading.value = false;
  });

  const getErrorMessage = (error: AuthError) => {
    switch (error.code) {
      case 'auth/invalid-credential':
        return 'Credenciales inválidas. Por favor, verifique su email y contraseña.';
      case 'auth/user-not-found':
        return 'Usuario no encontrado.';
      case 'auth/wrong-password':
        return 'Contraseña incorrecta.';
      case 'auth/too-many-requests':
        return 'Demasiados intentos fallidos. Por favor, intente más tarde.';
      case 'auth/user-disabled':
        return 'Esta cuenta ha sido deshabilitada.';
      default:
        return 'Error al iniciar sesión. Por favor, intente nuevamente.';
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      user.value = userCredential.user;
      notificationStore.success('Inicio de sesión exitoso');
      return true;
    } catch (error) {
      const authError = error as AuthError;
      notificationStore.error(getErrorMessage(authError));
      return false;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      user.value = null;
      userRole.value = null;
      notificationStore.success('Sesión cerrada correctamente');
    } catch (error) {
      console.error('Error signing out:', error);
      notificationStore.error('Error al cerrar sesión');
    }
  };

  const isAdmin = () => userRole.value === 'admin';

  return {
    user,
    userRole,
    loading,
    signIn,
    signOut,
    isAdmin
  };
});